import React, { useEffect, useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { fmtPrice, waLink, saveInquiry, skuFromHandle, productUrl } from "../lib/store";
import { trackInquiry, trackContactClick } from "../lib/analytics";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CreditNotice } from "./CreditNotice";
import { RegalClosing } from "./RegalClosing";
import { toast } from "sonner";

export function InquiryDialog({ product, open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) setSent(false);
  }, [open]);

  const sku = product?.handle ? skuFromHandle(product.handle) : null;
  const link = product?.handle ? productUrl(product.handle) : null;
  const closingCtx = /bridal|mangalsutra/i.test(product?.title || "")
    ? "bridal"
    : product?.categories?.includes("gold")
      ? "gold"
      : "default";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Please add your name and phone number");
    try {
      await saveInquiry({ ...form, product: product?.title, handle: product?.handle, sku, link });
      trackInquiry(product?.title);
      toast.success("Inquiry sent! We will get back to you within 24 hours.");
      setForm({ name: "", phone: "", message: "" });
      setSent(true);
    } catch (err) {
      toast.error("Could not send inquiry. Please check your email and try again.");
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#fffdf8] border-[#e8e0d0] rounded-none sm:max-w-md max-h-[85vh] overflow-y-auto">
        {sent ? (
          <div className="py-2" data-testid="inquiry-success">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-wide text-[#1a1713]">Inquiry Received</DialogTitle>
              <DialogDescription className="text-sm text-[#7a7160]">We will get back to you within 24 hours.</DialogDescription>
            </DialogHeader>
            <RegalClosing context={closingCtx} className="mt-5" />
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full mt-6 rounded-none bg-[#171310] hover:bg-[#3a2f1d] text-[#e9d9ac] tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300"
              data-testid="inquiry-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl tracking-wide text-[#1a1713]">Inquire About This Piece</DialogTitle>
              <DialogDescription className="text-sm text-[#7a7160]">{product?.title}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Name *</Label>
                  <Input data-testid="inquiry-name-input" value={form.name} onChange={set("name")} className="rounded-none border-[#d8cdb5]" placeholder="Your name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Phone / WhatsApp *</Label>
                  <Input data-testid="inquiry-phone-input" value={form.phone} onChange={set("phone")} className="rounded-none border-[#d8cdb5]" placeholder="(773) 000-0000" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-[#7a7160]">Message</Label>
                <Textarea data-testid="inquiry-message-input" value={form.message} onChange={set("message")} rows={3} className="rounded-none border-[#d8cdb5]" placeholder="I'd like to know more about availability, sizing, pricing..." />
              </div>
              <CreditNotice placement="inquiry" className="pt-1" />
              <div className="flex gap-3 pt-1">
                <a
                  onClick={() => trackContactClick("whatsapp_inquiry")}
                  href={waLink(
                    `Hello Regal Jewels! I'm interested in: ${product?.title} (${fmtPrice(product?.price || 0)})` +
                    (sku ? `\nSKU: ${sku}` : "") +
                    (link ? `\n${link}` : "") +
                    (form.message ? `\n\n${form.message}` : "")
                  )}
                  target="_blank" rel="noopener noreferrer" className="flex-1"
                >
                  <Button type="button" className="w-full rounded-none bg-[#25D366] hover:bg-[#1fb457] text-white tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300" data-testid="inquiry-whatsapp-btn">
                    <MessageCircle size={14} className="mr-2" /> WhatsApp Us
                  </Button>
                </a>
                <Button type="submit" variant="outline" className="flex-1 rounded-none border-[#1a1713] text-[#1a1713] hover:bg-[#1a1713] hover:text-[#f3ead2] tracking-[0.12em] uppercase text-xs h-11 transition-colors duration-300" data-testid="inquiry-submit-btn">
                  <Send size={14} className="mr-2" /> Send Inquiry
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
